import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"
import chroma from 'chroma-js'

export default class extends Controller {
  static values = {
    id: String,
    options: Object,
    series: Array,
    container: String
  }

  connect() {
    const container = document.querySelector(`#${this.containerValue}`)
    if (!container) return

    const primaryColor = getComputedStyle(document.documentElement)
      .getPropertyValue('--color-primary-500')
      .trim()

    const colors = this.generateMonochromaticPalette(primaryColor)

    let config = {
      ...this.optionsValue,
      series: this.seriesValue,
      ...(this.optionsValue.colors ? {} : { colors: colors })
    }

    // Aplicar formateadores personalizados y convertir funciones
    config = this.applyFormatters(config)
    config = this.parseFunctions(config)

    try {
      this.chart = new ApexCharts(container, config)
      this.chart.render()
    } catch (error) {
      console.error('Error al renderizar el gráfico:', error)
    }
  }

  parseFunctions(obj) {
    if (!obj || typeof obj !== 'object') return obj

    for (let key in obj) {
      if (typeof obj[key] === 'string' && obj[key].trim().startsWith('function')) {
        try {
          obj[key] = new Function('return ' + obj[key])()
        } catch (error) {
          console.error(`Error al parsear la función ${key}:`, error)
        }
      } else if (typeof obj[key] === 'object') {
        obj[key] = this.parseFunctions(obj[key])
      }
    }
    return obj
  }

  applyFormatters(obj) {
    if (!obj || typeof obj !== 'object') return obj

    for (let key in obj) {
      if (obj[key]?.format) {
        const format = obj[key].format
        obj[key] = {
          ...obj[key],
          formatter: `function(val, opts) {
            const value = Array.isArray(val) ? val[1] : val
            ${this.getFormatterCode(format)}
          }`
        }
      }

      if (typeof obj[key] === 'object') {
        obj[key] = this.applyFormatters(obj[key])
      }
    }
    return obj
  }

  getFormatterCode(format) {
    switch (format) {
      case 'range_percentage':
        return 'return opts.seriesIndex === 0 ? `${Number(value).toFixed(1)}%` : ""'
      case 'percentage':
        return 'return `${Number(value).toFixed(1)}%`'
      case 'currency':
        return 'return new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP" }).format(value)'
      case 'decimal':
        return 'return Number(value).toFixed(1)'
      case 'integer':
        return 'return Math.floor(value)'
      case 'compact':
        return 'return new Intl.NumberFormat("es-CL", { notation: "compact", maximumFractionDigits: 1 }).format(value)'
      default:
        return 'return value'
    }
  }

  // Analogous (colores adyacentes)
  generateAnalogousPalette(baseColor) {
    return [
      baseColor,
      chroma(baseColor).set('hsl.h', '-30').hex(),
      chroma(baseColor).set('hsl.h', '+30').hex(),
      chroma(baseColor).set('hsl.h', '-60').hex(),
      chroma(baseColor).set('hsl.h', '+60').hex(),
      chroma(baseColor).brighten(1).set('hsl.h', '-30').hex(),
      chroma(baseColor).brighten(1).set('hsl.h', '+30').hex()
    ]
  }

  // Monochromatic (variaciones del mismo color)
  generateMonochromaticPalette(baseColor) {
    return [
      baseColor,
      chroma(baseColor).brighten(2).hex(),
      chroma(baseColor).brighten(1).hex(),
      chroma(baseColor).darken(1).hex(),
      chroma(baseColor).darken(2).hex(),
      chroma(baseColor).brighten(1.5).hex(),
      chroma(baseColor).darken(1.5).hex()
    ]
  }

  // Split Complementary
  generateSplitComplementaryPalette(baseColor) {
    return [
      baseColor,
      chroma(baseColor).set('hsl.h', '+150').hex(),
      chroma(baseColor).set('hsl.h', '-150').hex(),
      chroma(baseColor).brighten(1).hex(),
      chroma(baseColor).darken(1).hex(),
      chroma(baseColor).set('hsl.h', '+150').brighten(1).hex(),
      chroma(baseColor).set('hsl.h', '-150').darken(1).hex()
    ]
  }

  // Triadic
  generateTriadicPalette(baseColor) {
    return [
      baseColor,
      chroma(baseColor).set('hsl.h', '+120').hex(),
      chroma(baseColor).set('hsl.h', '-120').hex(),
      chroma(baseColor).brighten(1).hex(),
      chroma(baseColor).set('hsl.h', '+120').brighten(1).hex(),
      chroma(baseColor).set('hsl.h', '-120').brighten(1).hex(),
      chroma(baseColor).darken(1).hex()
    ]
  }

  // Square
  generateSquarePalette(baseColor) {
    return [
      baseColor,
      chroma(baseColor).set('hsl.h', '+90').hex(),
      chroma(baseColor).set('hsl.h', '+180').hex(),
      chroma(baseColor).set('hsl.h', '+270').hex(),
      chroma(baseColor).brighten(1).hex(),
      chroma(baseColor).set('hsl.h', '+90').brighten(1).hex(),
      chroma(baseColor).set('hsl.h', '+180').brighten(1).hex()
    ]
  }

  // Tetradic
  generateTetradicPalette(baseColor) {
    return [
      baseColor,
      chroma(baseColor).set('hsl.h', '+60').hex(),
      chroma(baseColor).set('hsl.h', '+180').hex(),
      chroma(baseColor).set('hsl.h', '+240').hex(),
      chroma(baseColor).brighten(1).hex(),
      chroma(baseColor).set('hsl.h', '+60').brighten(1).hex(),
      chroma(baseColor).set('hsl.h', '+180').brighten(1).hex()
    ]
  }

  // Accent
  generateAccentPalette(baseColor) {
    return [
      baseColor,
      chroma(baseColor).set('hsl.h', '+90').saturate(2).hex(),
      chroma(baseColor).set('hsl.h', '-90').saturate(2).hex(),
      chroma(baseColor).brighten(2).hex(),
      chroma(baseColor).darken(1).hex(),
      chroma(baseColor).set('hsl.h', '+45').saturate(1.5).hex(),
      chroma(baseColor).set('hsl.h', '-45').saturate(1.5).hex()
    ]
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
  }
}
